import React, { useState, useEffect, useRef, ChangeEvent, KeyboardEvent, useCallback } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import Label from '../../components/Label';
import Loading from '../../components/Loading';

export interface OTPDialogProps {
  mode: 'dialog' | 'page';
  title: string;
  description: string;
  show?: boolean; // For dialog mode
  onVerify: (otp: string) => Promise<void>;
  onResend: () => Promise<void>;
  onCancel?: () => void;
  autoFocus?: boolean;
  length?: number;
  resendCooldown?: number; // Seconds before allowing resend
  expirationTime?: number; // Seconds until OTP expires
}

const OTPDialog: React.FC<OTPDialogProps> = ({
  mode = 'dialog',
  title,
  description,
  show = false,
  onVerify,
  onResend,
  onCancel,
  autoFocus = true,
  length = 6,
  resendCooldown = 60,
  expirationTime = 0, // Default to no expiration
}) => {
  const [otpDigits, setOtpDigits] = useState<string[]>(Array(length).fill(''));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [message, setMessage] = useState(description);
  const [timeRemaining, setTimeRemaining] = useState(expirationTime);
  const [isExpired, setIsExpired] = useState(false);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const focusFirstInput = useCallback(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const resetInput = useCallback(() => {
    setOtpDigits(Array(length).fill(''));
    focusFirstInput();
  }, [length, focusFirstInput]);

  // Auto-focus on mount and when showing
  useEffect(() => {
    if (autoFocus && (mode === 'page' || show)) {
      focusFirstInput();
    }
  }, [autoFocus, mode, show, focusFirstInput]);

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Expiration timer countdown
  useEffect(() => {
    if (expirationTime > 0 && timeRemaining > 0 && !success && !isExpired) {
      const timer = setTimeout(() => {
        const newTime = timeRemaining - 1;
        setTimeRemaining(newTime);
        if (newTime === 0) {
          setIsExpired(true);
          setError('Code has expired');
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [timeRemaining, expirationTime, success, isExpired]);

  // Reset expiration timer when resending OTP
  useEffect(() => {
    if (resendTimer === resendCooldown && expirationTime > 0) {
      setTimeRemaining(expirationTime);
      setIsExpired(false);
    }
  }, [resendTimer, resendCooldown, expirationTime]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>, index: number) => {
    const { value } = event.target;
    const newOtpDigits = [...otpDigits];

    // Only allow a single numeric digit
    if (/^[0-9]$/.test(value)) {
      newOtpDigits[index] = value;
      setOtpDigits(newOtpDigits);
      setError('');

      // Move focus to the next input if available
      if (index < length - 1 && inputRefs.current[index + 1]) {
        inputRefs.current[index + 1]?.focus();
      }

      // If all digits are entered, automatically submit
      const currentOtp = newOtpDigits.join('');
      if (currentOtp.length === length && newOtpDigits.every(digit => digit !== '')) {
        handleSubmit(currentOtp);
      }
    } else if (value === '') {
      // Allow clearing the current input
      newOtpDigits[index] = '';
      setOtpDigits(newOtpDigits);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (event.key === 'Backspace' && otpDigits[index] === '') {
      // If backspace is pressed on an empty input, move focus to the previous input
      if (index > 0 && inputRefs.current[index - 1]) {
        inputRefs.current[index - 1]?.focus();
        event.preventDefault();
      }
    } else if (event.key === 'Enter') {
      const currentOtp = otpDigits.join('');
      if (currentOtp.length === length) {
        handleSubmit(currentOtp);
      }
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pasteData = event.clipboardData.getData('text').trim();
    if (/^[0-9]*$/.test(pasteData) && pasteData.length <= length) {
      const newOtpDigits = Array(length).fill('');
      for (let i = 0; i < pasteData.length; i++) {
        newOtpDigits[i] = pasteData[i];
      }
      setOtpDigits(newOtpDigits);
      setError('');

      // Move focus to the last filled input or the last input if all are filled
      const lastFilledIndex = Math.min(pasteData.length - 1, length - 1);
      if (inputRefs.current[lastFilledIndex]) {
        inputRefs.current[lastFilledIndex]?.focus();
      }

      // If a complete OTP was pasted, submit it
      if (pasteData.length === length) {
        handleSubmit(newOtpDigits.join(''));
      }
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (otpValue?: string) => {
    const otp = otpValue || otpDigits.join('');
    
    if (otp.length !== length) {
      setError(`Please enter a ${length}-digit code`);
      return;
    }

    if (isExpired) {
      setError('Code has expired');
      return;
    }

    setError('');
    setLoading(true);
    
    try {
      await onVerify(otp);
      setSuccess(true);
      setMessage('Verification successful!');
      // Don't clear OTP on success - let parent handle navigation
    } catch (err: any) {
      setError(err.message || 'Invalid code');
      resetInput(); // Clear on error
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    
    setLoading(true);
    setError('');
    
    try {
      await onResend();
      setMessage('A new verification code has been sent to your email.');
      setResendTimer(resendCooldown);
      resetInput();
    } catch (err: any) {
      setError(err.message || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const content = (
    <div className={mode === 'page' ? 'otp-page-container' : ''}>
      {mode === 'page' && <h4 style={{ textAlign: 'center' }}>{title}</h4>}
      
      <p style={{ textAlign: 'center', marginBottom: 20 }}>{message}</p>
      
      {!success && (
        <Form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          <Form.Group className="mb-4">
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              {otpDigits.map((digit, index) => (
                <input
                  key={index}
                  type="text"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(e, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onPaste={handlePaste}
                  ref={(el) => (inputRefs.current[index] = el)}
                  disabled={loading || isExpired}
                  inputMode="numeric"
                  pattern="[0-9]"
                  autoComplete={index === 0 ? "one-time-code" : "off"}
                  style={{
                    width: '45px',
                    height: '50px',
                    fontSize: '24px',
                    textAlign: 'center',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    boxSizing: 'border-box',
                    fontWeight: '500',
                    backgroundColor: loading ? '#f8f9fa' : 'white',
                    transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                    outline: 'none',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#0d6efd';
                    e.target.style.boxShadow = '0 0 0 0.25rem rgba(13, 110, 253, 0.25)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#dee2e6';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              ))}
            </div>
          </Form.Group>

          <div style={{ height: '24px', marginBottom: '16px' }}>
            {error && (
              <p style={{ 
                color: '#dc3545', 
                textAlign: 'center', 
                margin: 0,
                fontSize: '14px'
              }}>
                {error}
              </p>
            )}
          </div>

          {loading && (
            <div className="text-center mb-3">
              <Loading />
              <p className="mt-2 text-muted">Verifying code...</p>
            </div>
          )}

          <div className="d-grid gap-2">
            {expirationTime > 0 ? (
              // Show expiration timer or resend button based on whether code is expired
              isExpired ? (
                <Button
                  variant="link"
                  onClick={handleResend}
                  disabled={loading || resendTimer > 0}
                >
                  {resendTimer > 0
                    ? `Resend code in ${resendTimer}s`
                    : 'Resend Code'}
                </Button>
              ) : (
                <div style={{ textAlign: 'center', padding: '8px' }}>
                  <small style={{ 
                    color: timeRemaining <= 60 ? '#dc3545' : '#6c757d',
                    fontWeight: timeRemaining <= 60 ? '500' : 'normal'
                  }}>
                    Code expires in: <strong>{formatTime(timeRemaining)}</strong>
                  </small>
                </div>
              )
            ) : (
              // No expiration time set, show regular resend button
              <Button
                variant="link"
                onClick={handleResend}
                disabled={loading || resendTimer > 0}
              >
                {resendTimer > 0
                  ? `Resend code in ${resendTimer}s`
                  : 'Resend Code'}
              </Button>
            )}
          </div>
        </Form>
      )}

      {success && (
        <div style={{ textAlign: 'center' }}>
          <Label fontWeight="bold" size="large" color="green">
            {`✓ ${message}`}
          </Label>
          {mode === 'page' && <Loading center />}
        </div>
      )}
    </div>
  );

  if (mode === 'dialog') {
    return (
      <Modal show={show} onHide={onCancel} centered>
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{content}</Modal.Body>
      </Modal>
    );
  }

  return content;
};

export default OTPDialog;